import { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { ArrowUpDown } from "lucide-react";

const fmtINR = (v) => {
  if (v == null) return "-";
  const abs = Math.abs(v);
  if (abs >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(1)} Cr`;
  if (abs >= 1_00_00_000) return `${(v / 1_00_00_000).toFixed(1)} Cr`;
  if (abs >= 1_00_000) return `${(v / 1_00_000).toFixed(1)} L`;
  return v.toLocaleString("en-IN");
};

const LINE_ITEMS = [
  { label: "Revenue", key: "revenue", bold: true },
  { label: "Cost of Goods Sold", key: "cogs" },
  { label: "Gross Profit", key: "gross_profit", bold: true, highlight: true },
  { label: "Selling, General & Admin", key: "sga_expense" },
  { label: "Research & Development", key: "rd_expense" },
  { label: "Depreciation", key: "depreciation" },
  { label: "Other Operating Expenses", key: "other_opex" },
  { label: "Total Operating Expenses", key: "total_opex", bold: true },
  { label: "Operating Income (EBIT)", key: "operating_income", bold: true, highlight: true },
  { label: "Interest Expense", key: "interest_expense" },
  { label: "Other Income", key: "other_income" },
  { label: "Profit Before Tax", key: "pbt", bold: true },
  { label: "Income Tax", key: "tax" },
  { label: "Net Income", key: "net_income", bold: true, highlight: true },
  { label: "EBITDA", key: "ebitda", bold: true, highlight: true },
];

const IncomeStatement = ({ data, endPeriod, comparisonData, comparisonPeriod }) => {
  const [sortOrder, setSortOrder] = useState("asc");

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (!endPeriod) return data;
    const endIdx = parseInt(endPeriod, 10);
    if (!isNaN(endIdx) && endIdx >= 1 && endIdx <= data.length) {
      return data.slice(0, endIdx);
    }
    return data;
  }, [data, endPeriod]);

  const displayData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    return sortOrder === "asc" ? [...filteredData] : [...filteredData].reverse();
  }, [filteredData, sortOrder]);

  const compItem = useMemo(() => {
    if (!comparisonData || comparisonData.length === 0 || !comparisonPeriod || comparisonPeriod === "none") return null;
    const idx = parseInt(comparisonPeriod, 10) - 1;
    return (idx >= 0 && idx < comparisonData.length) ? comparisonData[idx] : comparisonData[comparisonData.length - 1];
  }, [comparisonData, comparisonPeriod]);

  if (!data || data.length === 0) return <p className="text-muted-foreground p-4">No data available</p>;

  const chartData = displayData.map((p) => ({
    period: p.period,
    Revenue: p.revenue,
    "Gross Profit": p.gross_profit,
    "Net Income": p.net_income,
  }));

  return (
    <div className="space-y-4" data-testid="income-statement">
      {/* Action Strip: Sort Toggle */}
      <div className="flex items-center justify-between gap-2 px-1">
        <span className="text-xs text-muted-foreground">
          Showing <strong>{displayData.length}</strong> periods ({sortOrder === "asc" ? "Chronological" : "Latest First"})
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5"
          onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          data-testid="is-sort-btn"
        >
          <ArrowUpDown className="w-3.5 h-3.5 text-primary" />
          Sort Periods: {sortOrder === "asc" ? "Oldest → Newest" : "Newest → Oldest"}
        </Button>
      </div>

      {/* Chart */}
      <div className="h-64 w-full min-w-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={(v) => fmtINR(v)} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmtINR(v)} />
            <Legend />
            <Bar dataKey="Revenue" fill="hsl(211,100%,32%)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="Gross Profit" fill="hsl(160,84%,39%)" radius={[2, 2, 0, 0]} />
            <Bar dataKey="Net Income" fill="hsl(38,92%,50%)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-background z-10 min-w-[200px]">Line Item</TableHead>
              {displayData.map((p) => (
                <TableHead key={p.period} className="text-right min-w-[110px]">{p.period}</TableHead>
              ))}
              {compItem && (
                <>
                  <TableHead className="text-right min-w-[125px] bg-muted/40 font-bold text-foreground">
                    Comp ({compItem.period})
                  </TableHead>
                  <TableHead className="text-right min-w-[110px] bg-muted/40 font-bold text-foreground">
                    Variance
                  </TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {LINE_ITEMS.map((item) => {
              const latestVal = displayData[displayData.length - 1]?.[item.key] ?? 0;
              const compVal = compItem?.[item.key] ?? 0;
              const variance = compItem ? latestVal - compVal : 0;
              return (
                <TableRow
                  key={item.key}
                  className={item.highlight ? "bg-primary/5" : ""}
                  data-testid={`is-row-${item.key}`}
                >
                  <TableCell className={`sticky left-0 bg-background z-10 ${item.bold ? "font-semibold" : ""} ${item.highlight ? "bg-primary/5" : ""}`}>
                    {item.label}
                  </TableCell>
                  {displayData.map((p) => (
                    <TableCell key={p.period} className={`text-right tabular-nums ${item.bold ? "font-semibold" : ""}`}>
                      {fmtINR(p[item.key])}
                    </TableCell>
                  ))}
                  {compItem && (
                    <>
                      <TableCell className={`text-right tabular-nums bg-muted/20 ${item.bold ? "font-semibold" : ""}`}>
                        {fmtINR(compVal)}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums bg-muted/20 font-medium ${
                          variance > 0 ? "text-emerald-600" : variance < 0 ? "text-red-600" : ""
                        }`}
                      >
                        {variance > 0 ? "+" : ""}{fmtINR(variance)}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default IncomeStatement;
